import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User, LogOut, Settings, Sliders, Volume2, Sparkles, Target } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { AppState } from '../../app/types';
import { GroupId, StartingPeriod } from '../../config/systemConfig';
import { ProfileSection, type AccountsState } from '../../accounts';
import { PageContainer } from '../shared/PageContainer';
import { PageHero } from '../shared/PageHero';
import { WorldZone } from '../shared/WorldZone';
import { itemVariants } from '../shared/animations';
import { useDjaySelection } from '../../mascot';
import { dataLayer, type LearnerPreferences } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
const DEFAULT_PREFS: LearnerPreferences = {
  sound: true,
  reducedMotion: false,
  dailyGoal: 10
};
interface SettingsPageProps {
  state: AppState;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  manualPeriod?: StartingPeriod | null;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  onUpdateName?: (newName: string) => void;
  accounts?: AccountsState;
  onLogout?: () => void;
  viewingLearnerId?: string | null;
}
export function SettingsPage({
  state,
  selectedGroup,
  startingPeriod,
  manualPeriod,
  onSetStartingPeriod,
  onUpdateName,
  accounts,
  onLogout,
  viewingLearnerId
}: SettingsPageProps) {
  const {
    resetLearnerProgress
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Persisted preferences — loaded async from dataLayer
  const [prefs, setPrefs] = useState<LearnerPreferences>(DEFAULT_PREFS);
  // Load preferences from dataLayer on mount
  useEffect(() => {
    dataLayer.getLearnerPreferences().then(setPrefs);
  }, []);
  // Save preferences to dataLayer on change
  useEffect(() => {
    dataLayer.saveLearnerPreferences(prefs);
  }, [prefs]);
  const hasMasteryData = Object.values(state.familyMastery || {}).some(m => m && m.attempts > 0);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const handleResetProgress = () => setResetConfirmOpen(true);
  const confirmResetProgress = () => {
    const learnerId = viewingLearnerId || accounts?.activeAccountId;
    if (learnerId) {
      resetLearnerProgress(learnerId);
      onSetStartingPeriod(null);
    }
    setResetConfirmOpen(false);
  };
  const updatePref = <K extends keyof LearnerPreferences,>(key: K, value: LearnerPreferences[K]) => {
    setPrefs(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const activeAccount = accounts ? accounts.accountsById[accounts.activeAccountId] : null;
  const isViewingLearner = !!viewingLearnerId && (activeAccount?.role === 'parent' || activeAccount?.role === 'teacher');
  const displayName = isViewingLearner ? state.stats.name : activeAccount?.displayName || state.stats.name;
  const accountRole = activeAccount?.role || 'learner';
  const dailyGoalOptions: {
    value: 5 | 10 | 20;
    emoji: string;
    label: string;
  }[] = [{
    value: 5,
    emoji: '🌱',
    label: 'Rustig'
  }, {
    value: 10,
    emoji: '⭐',
    label: 'Standaard'
  }, {
    value: 20,
    emoji: '🚀',
    label: 'Uitdaging'
  }];
  return <PageContainer data-id="element-1332">
      <motion.div variants={itemVariants} data-id="element-1333">
        <PageHero title="Instellingen" subtitle="Pas je profiel en voorkeuren aan" gradientClasses="from-orange-500 via-amber-500 to-yellow-500" icon={Settings} iconBgClass="bg-orange-400" djayEmotion="happy" djayVariant={selectedVariant} floatingEmojis={['✨', '🎨']} data-id="element-1334" />
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1335">
        <WorldZone title="Profiel" icon={User} accentColor="bg-orange-100 text-orange-600" isLast={false} data-id="element-1336">
          <ProfileSection stats={state.stats} displayName={state.stats.name} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onSetStartingPeriod={onSetStartingPeriod} onUpdateName={onUpdateName} accountRole={accounts?.accountsById[accounts.activeAccountId]?.role} isViewingLearner={!!viewingLearnerId} accounts={accounts} viewingLearnerId={viewingLearnerId} hasMasteryData={hasMasteryData} onResetProgress={handleResetProgress} data-id="element-1337" />
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1338">
        <WorldZone title="Voorkeuren" icon={Sliders} accentColor="bg-amber-100 text-amber-600" isLast={false} data-id="element-1339">
          <div className="space-y-2" data-id="element-1340">
            {/* Sound Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-1341">
              <div className="flex items-center gap-4" data-id="element-1342">
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shadow-sm border border-sky-100" data-id="element-1343">
                  <Volume2 className="w-5 h-5" data-id="element-1344" />
                </div>
                <div data-id="element-1345">
                  <div className="font-bold text-slate-900" data-id="element-1346">
                    Geluidseffecten
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-1347">
                    Geluidjes bij goede antwoorden en beloningen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.sound} onChange={v => updatePref('sound', v)} label="Geluidseffecten" variant="student" data-id="element-1348" />
            </div>

            {/* Reduced Motion Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-1349">
              <div className="flex items-center gap-4" data-id="element-1350">
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shadow-sm border border-purple-100" data-id="element-1351">
                  <Sparkles className="w-5 h-5" data-id="element-1352" />
                </div>
                <div data-id="element-1353">
                  <div className="font-bold text-slate-900" data-id="element-1354">
                    Animaties verminderen
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-1355">
                    Rustiger beeld zonder bewegende effecten
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.reducedMotion} onChange={v => updatePref('reducedMotion', v)} label="Animaties verminderen" variant="student" data-id="element-1356" />
            </div>

            {/* Daily Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-1357">
              <div className="flex items-center gap-4 mb-5" data-id="element-1358">
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shadow-sm border border-amber-100" data-id="element-1359">
                  <Target className="w-5 h-5" data-id="element-1360" />
                </div>
                <div data-id="element-1361">
                  <div className="font-bold text-slate-900" data-id="element-1362">Dagelijks doel</div>
                  <div className="text-sm text-slate-600" data-id="element-1363">
                    Hoeveel vragen wil je per dag oefenen?
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3" data-id="element-1364">
                {dailyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('dailyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.dailyGoal === opt.value ? 'border-amber-400 bg-amber-50 text-amber-800 shadow-[0_2px_0_0_rgb(251,191,36)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-amber-200 hover:bg-amber-50/50'}
                    `} aria-pressed={prefs.dailyGoal === opt.value} data-id="element-1365">
                    <div className="text-2xl mb-1" data-id="element-1366">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-1367">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-1368">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1369">
        <WorldZone title="Afsluiten" icon={LogOut} accentColor="bg-rose-100 text-rose-600" isLast={true} data-id="element-1370">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4" data-id="element-1371">
            <div data-id="element-1372">
              <h3 className="text-lg font-black text-slate-800" data-id="element-1373">Uitloggen</h3>
              <p className="text-slate-600 font-medium" data-id="element-1374">
                Klaar voor vandaag? Tot de volgende keer!
              </p>
            </div>
            <Button onClick={onLogout} variant="secondary" className="w-full sm:w-auto text-rose-600 border-rose-200 hover:bg-rose-600 hover:text-white hover:border-rose-600" data-id="element-1375">
              <LogOut className="w-5 h-5 mr-2" data-id="element-1376" />
              Uitloggen
            </Button>
          </div>
        </WorldZone>
      </motion.div>
      <ConfirmDialog open={resetConfirmOpen} title="Voortgang wissen" message="Weet je zeker dat je alle voortgang wilt wissen? Dit kan niet ongedaan worden gemaakt." confirmLabel="Voortgang wissen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmResetProgress} onCancel={() => setResetConfirmOpen(false)} data-id="element-1377" />
    </PageContainer>;
}